// URL <-> view-state mapping for the flow gallery SPA, so refreshes and the
// browser Back/Forward buttons work. Each full-page view maps to a path segment
// under the shop base (e.g. /shop/builder); the transient selection (pose,
// package variant, sibling student) rides in the query string so a deep link
// like /shop/builder?pose=123&variant=45&student=678 restores exactly.
//
// Rails mounts the same island for every one of these paths (see the shop
// routes), so navigating between views never hits the server.

// All full-page views, in path form.
export const VIEWS = [
  'gallery', 'packages', 'builder', 'cart',
  'retouching', 'personalized', 'yearbook', 'pastphotos', 'siblings', 'printess',
];

// Views a COLD load (refresh / direct link) can faithfully restore. The upsell
// steps depend on mid-purchase state that isn't in the URL (which cart line to
// attach retouching to, the Printess editor session, the upsell chain), so a
// refresh on one of those lands on the cart instead of a half-restored step.
const COLD_RESTORABLE = new Set(['gallery', 'packages', 'builder', 'cart']);

// Which views carry which selection in the query string.
// `personalized` carries the pose because every card on it is a render of THAT
// photo on a product -- without one the step has nothing to preview.
const POSE_VIEWS = new Set(['packages', 'builder', 'printess', 'personalized']);
const VARIANT_VIEWS = new Set(['builder']);

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

// The view path segment from a pathname, relative to basePath. Unknown or
// missing segments read as 'gallery'.
const viewFromPath = (pathname, basePath) => {
  const rest = pathname.startsWith(basePath) ? pathname.slice(basePath.length) : pathname;
  const seg = rest.replace(/^\/+/, '').split('/')[0];
  return VIEWS.includes(seg) ? seg : 'gallery';
};

// Parse the current location into a view + pending selection ids.
// `cold` applies the COLD_RESTORABLE fallback (upsells -> cart) for a fresh load;
// popstate within a session passes cold=false so Back can revisit any view.
export const parseLocation = (location, basePath, { cold = false } = {}) => {
  const rawView = viewFromPath(location.pathname, basePath);
  const params = new URLSearchParams(location.search || '');
  const poseId = POSE_VIEWS.has(rawView) ? toInt(params.get('pose')) : null;
  const variantId = VARIANT_VIEWS.has(rawView) ? toInt(params.get('variant')) : null;
  const studentId = toInt(params.get('student'));
  let view = rawView;
  if (cold && !COLD_RESTORABLE.has(rawView)) view = rawView === 'gallery' ? 'gallery' : 'cart';
  return {
    view,
    poseId,
    variantId,
    studentId,
    // A cold load only needs to wait on data when it must resolve a pose/variant.
    needsRestore: view === rawView && (poseId != null || variantId != null),
  };
};

// Build the path+query string for a given view + selection. Ids are only
// included for the views that use them, and `student` only when it differs from
// the entered student (keeps the common gallery URL clean).
export const buildLocation = ({
  basePath, view, pose, variant, student, defaultStudent,
}) => {
  const path = `${basePath}/${view}`;
  const params = new URLSearchParams();
  if (POSE_VIEWS.has(view) && pose && pose.id != null) params.set('pose', pose.id);
  if (VARIANT_VIEWS.has(view) && variant && variant.id != null) params.set('variant', variant.id);
  if (student != null && defaultStudent != null && student !== defaultStudent) {
    params.set('student', student);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};
